import React, { Fragment, PureComponent, Suspense } from 'react'
import { Redirect } from 'react-router-dom'
import track from 'react-tracking'
import { sanitize } from 'dompurify'
import PropTypes from 'prop-types'
import BEMHelper from '@ynap/bem-helper'
import { GuestRegistrationForm } from '@ynap/login-and-registration-form'
import { ToggleSection } from '@ynap/toggle'
import { ScrollToTopOnMount } from '@ynap/scroll-to-top'
import { LineItem, LineItemGroup } from '@ynap/line-item'
import Address from '@ynap/address'
import Totals from '@ynap/totals'
import PaymentCard from '@ynap/payment-card'
import Price from '../Price/Price'
import { Notification } from '@ynap/notification'
import { getCookie } from '@ynap/redux-cookies-middleware/src/cookieApi'
import createAnalyticsEvent, { trackConsideredLinkClick } from '../analytics'
import SustainabilityInfo from '../SustainabilityInfo'
const DonationGiving = React.lazy(() => import(/* webpackChunkName: "DonationGiving" */ '../DonationGiving'))
import buyapowaTracking, { isBuyapowaCode } from './buyapowa-tracking'
import getCardHolder from '../../utils/getCardHolder'
import ConsideredInfo from '../ConsideredInfo/ConsideredInfo'
import { withOptimizely, trackOrder as trackOrderOptimizely } from '@ynap/optimizely-utils'
import mockState from './devModeMockState'

const bem = new BEMHelper('OrderConfirmation')

const propTypes = {
    component: PropTypes.string,
    className: PropTypes.string,
    cardTypeInfo: PropTypes.object,
    messages: PropTypes.shape({
        orderConfirmation: PropTypes.shape({
            thankyou: PropTypes.func.isRequired,
            orderRef: PropTypes.func.isRequired,
            email: PropTypes.func.isRequired,
            shippingTo: PropTypes.func.isRequired,
            shippingMethod: PropTypes.func.isRequired,
            billedTo: PropTypes.func.isRequired,
        }),
        orderReview: PropTypes.shape({
            title: PropTypes.func.isRequired,
            summaryTitle: PropTypes.func.isRequired,
        }),
    }).isRequired,
    orderRef: PropTypes.string,
    shippingAddress: PropTypes.object,
    shippingOption: PropTypes.object,
    billingAddress: PropTypes.object,
    paymentOption: PropTypes.object,
    lineItems: PropTypes.object,
    numItems: PropTypes.number.isRequired,
    totals: PropTypes.object,
    userStatus: PropTypes.string,
    clearCart: PropTypes.func,
    register: PropTypes.func,
    locale: PropTypes.object,
    showRegistrationForm: PropTypes.bool,
    deviceType: PropTypes.string,
    email: PropTypes.string,
    donateToCharity: PropTypes.func,
    enableConsidered: PropTypes.bool,
    checkoutAnalytics: PropTypes.object,
    optimizely: PropTypes.object,
    applePayAddressDetails: PropTypes.object,
}

const defaultProps = {
    component: 'div',
    userStatus: 'G',
    cardTypeInfo: {},
}

@track()
class OrderConfirmation extends PureComponent {
    constructor(props) {
        super(props)

        // Cache order details in component as cart state will be cleared on mount
        const {
            orderRef,
            shippingAddress,
            shippingOption,
            signatureRequired,
            paymentOption,
            billingAddress,
            userStatus,
            lineItems,
            numItems,
            totals,
            deviceType,
            email,
            currentPackagingOption,
            saleItemsInOrder,
            cardTypeInfo,
            links,
            orderConfirmationMockEnabled,
            multipleLanguageCountries,
            gdprLikeCountries,
            ageConsentCountries,
            tcConsentCountries,
        } = this.props

        this.state = {
            orderRef,
            shippingAddress,
            shippingOption,
            signatureRequired,
            paymentOption,
            billingAddress,
            userStatus,
            lineItems,
            numItems,
            totals,
            deviceType,
            email,
            currentPackagingOption,
            saleItemsInOrder,
            cardTypeInfo,
            donationAvailable: false,
            donationCharities: [],
            links,
            orderConfirmationMockEnabled,
            multipleLanguageCountries,
            gdprLikeCountries,
            ageConsentCountries,
            tcConsentCountries,
        }
        this.handleSubmit = this.handleSubmit.bind(this)
    }

    @track(({ checkoutAnalytics = {} }, state) => ({
        ...createAnalyticsEvent({
            ...checkoutAnalytics,
            cardTypeInfo: state?.cardTypeInfo,
            pageConfigs: {
                pageName: 'checkout - order confirmation',
                pageType: 'checkout',
                primaryCategory: 'purchase',
                subCategory1: 'checkout',
                subCategory2: 'order confirmation',
            },
        }),
        lpTag: { customParam: 'transaction' },
    }))
    componentDidMount() {
        const { clearCart, optimizely, checkoutAnalytics, fetchCountries } = this.props
        const { userStatus } = this.state
        optimizely && checkoutAnalytics && trackOrderOptimizely(optimizely, checkoutAnalytics)
        fetchCountries()

        //track buyapowa referral
        this.trackBuyapowa()
        this.setState({
            donationOrderId: this.props.donationOrderId,
            donationCharities: this.props.donationCharities,
            donationAvailable: this.props.donationAvailable,
        })
        !this.state.orderConfirmationDevMode && clearCart()
        this.addQueryParamForNativeWebView()

        optimizely?.onReady?.()?.then?.(() => {
            optimizely?.setUser?.({
                id: optimizely?.user?.id,
                attributes: {
                    ...optimizely?.user?.attributes,
                    logged_in: userStatus === 'R',
                },
            })
        })
    }

    trackBuyapowa() {
        const { buyapowaConfig = {}, locale, promotionCode } = this.props

        const buyapowaCode = isBuyapowaCode(promotionCode)

        if (buyapowaCode && (buyapowaConfig.enableBuyapowa || buyapowaConfig.enableBuyapowaInCountries?.includes(locale.country))) {
            // buyapowaMarketDomain: https://bp.mrporter.com or https://bp-au.theoutnet.com
            // buyapowaMarket: 'mrporter' or 'outnet-au'
            const country = locale.country.toLowerCase()
            const marketDomain = buyapowaConfig.marketDomain?.replace('{country}', country)
            const market = buyapowaConfig.market?.replace('{country}', country)
            const { orderRef } = this.state
            buyapowaTracking.track(marketDomain, market, orderRef, promotionCode)
            return true
        }
        return false
    }

    // TODO: This can be removed when ios apps implement native checkout
    // This is used to allow opening the confirmation page as a deep link in the app
    addQueryParamForNativeWebView() {
        const { history, orderRef } = this.props

        if (window.URLSearchParams) {
            const params = new URLSearchParams(history.location.search)
            params.set('orderId', orderRef)
            history.replace({ ...history.location, search: params.toString() })
        }
    }

    @track(({ checkoutAnalytics }, state, targetURL) => trackConsideredLinkClick(checkoutAnalytics, targetURL))
    onConsideredLinkClick(targetURL) {}

    handleSubmit(...args) {
        const { optimizely, register } = this.props
        optimizely?.track('oc_registration_submit')
        return register(...args)
    }

    render() {
        const {
            component: Component,
            locale,
            register,
            messages,
            className,
            continueShoppingPath,
            sustainabilityEnabled = false,
            donateToCharity,
            donationStatus,
            enableDonationGiving,
            enableConsidered,
            orderConfirmationDevMode,
            crisisMessages,
            optimizely,
        } = this.props

        const {
            orderRef,
            shippingAddress = {},
            shippingOption,
            signatureRequired,
            paymentOption = {},
            billingAddress = {},
            userStatus,
            lineItems,
            numItems,
            totals,
            email,
            currentPackagingOption,
            saleItemsInOrder,
            donationAvailable,
            donationCharities,
            donationOrderId,
            links,
            checkoutAnalytics,
            gdprLikeCountries,
            ageConsentCountries,
            tcConsentCountries,
            multipleLanguageCountries,
        } = orderConfirmationDevMode ? mockState : this.state
        // Dev Mode can be turned on by seeting yos_head_orderConfirmationDevMode=true on local config files /env/
        // This will use ./devModeMockState.js data so that Order Confirmation page can be reloaded while working on it

        const { personTitle, firstName, lastName } = billingAddress
        const dangerousHTML = messages.orderConfirmation.orderRef({ personTitle, lastName, orderRef: `<b>${orderRef}</b>` })
        const [paymentMethodId] = paymentOption?.id?.split('/') || []
        const cardHolder = getCardHolder(paymentOption.card_holder)
        const gdprConsent = !!gdprLikeCountries?.includes(locale?.country)
        const ageConsent = !!ageConsentCountries?.includes(locale?.country) && messages.registrationForm.ageConsent?.()
        const tcConsent = !!tcConsentCountries?.includes(locale?.country) && messages.registrationForm.tcConsent?.()

        const consideredMessaging = enableConsidered ? messages?.considered?.consideredInfo : null
        let ordersDelayedMessage = crisisMessages?.empty ? '' : crisisMessages?.orderConfirmation?.text

        let packagingMessage
        let packagingNotificationType

        if (currentPackagingOption?.name === 'Signature') {
            packagingMessage = messages.notifications.packagingInfo?.nextTime()
            packagingNotificationType = 'info'
        } else if (currentPackagingOption?.name === 'Basic') {
            packagingMessage = messages.notifications.packagingInfo?.thankYou()
            packagingNotificationType = 'success'
        }

        const genderPreferenceCookie = getCookie('ton-shop') || 'womens'
        const continueShoppingPathUrl = continueShoppingPath?.replace('{gender}', genderPreferenceCookie === 'mens' ? 'mens/' : '')

        const shouldSuggestRegistration = userStatus === 'G'

        return (
            <Component className={bem(null, null, className)}>
                {orderRef ? (
                    <Fragment>
                        <ScrollToTopOnMount />
                        <div className={bem('orderThankYou')}>
                            <h2 className={bem('header')}>{messages.orderConfirmation.thankyou({ lastName, firstName })}</h2>
                            <p className={bem('confirmationText')} dangerouslySetInnerHTML={{ __html: sanitize(dangerousHTML, { ALLOWED_TAGS: ['b'] }) }} />
                            <p className={bem('confirmationText')}>
                                {messages.orderConfirmation.email()}
                                <br />
                                <b>{email}</b>
                            </p>

                            {!ordersDelayedMessage && packagingMessage && !saleItemsInOrder && !sustainabilityEnabled && (
                                <Notification type={packagingNotificationType} className={bem('packagingInfoMessage')} message={packagingMessage} />
                            )}
                            {ordersDelayedMessage && <Notification type="info" className={bem('orderDelay')} message={ordersDelayedMessage} />}
                        </div>
                        {shouldSuggestRegistration && (
                            <GuestRegistrationForm
                                className={bem('registrationForm', 'registrationEnabled')}
                                onSubmit={this.handleSubmit}
                                initialValues={{
                                    logonId: email,
                                    personTitle,
                                    firstName,
                                    lastName,
                                    maMarketingConsent: 'false',
                                    gdprConsent: 'false',
                                    gender: 'Unspecified',
                                }}
                                messages={messages}
                                links={links}
                                continueShoppingPath={continueShoppingPathUrl}
                                checkoutAnalytics={checkoutAnalytics}
                                gdprConsent={gdprConsent}
                                ageConsent={ageConsent}
                                tcConsent={tcConsent}
                                supportedLanguages={multipleLanguageCountries?.[locale?.country]}
                                locale={locale}
                                enableGenderSelection={true}
                                optimizely={optimizely}
                            />
                        )}
                        {donationAvailable && enableDonationGiving && donationCharities?.length > 0 && (
                            <Suspense fallback={<div className="donation_placeholder" />}>
                                <DonationGiving
                                    orderId={donationOrderId}
                                    messages={messages}
                                    locale={locale}
                                    donateToCharity={donateToCharity}
                                    donationStatus={donationStatus}
                                    charityName={donationCharities[0]}
                                />
                            </Suspense>
                        )}
                        {enableConsidered && (
                            <ConsideredInfo
                                className={bem('consideredInfo', 'registrationEnabled')}
                                locale={locale}
                                orderConfirmation={true}
                                consideredBadge={messages.considered.badge()}
                                title={consideredMessaging.consideredPackaging.title()}
                                bodyTextOne={consideredMessaging.consideredPackaging.packagingInformation()}
                                bodyTextThree={consideredMessaging.consideredPackaging.careGuide()}
                                actionText={consideredMessaging.consideredPackaging.actionText(locale)}
                                href={consideredMessaging.consideredPackaging.href()}
                                hrefSuffix={consideredMessaging.consideredPackaging?.hrefSuffix?.()}
                                onLinkClick={(href) => this.onConsideredLinkClick(href)}
                            />
                        )}
                        <ToggleSection
                            className={bem('toggleSection', 'registrationEnabled')}
                            headerClassName={bem('toggleSectionHeader', 'registrationEnabled')}
                            type="heading"
                            title={messages.orderConfirmation.showOrderDetails({ numItems })}
                            defaultOpen={!shouldSuggestRegistration}
                            track={(open) => optimizely?.track(open ? 'oc_expand_details' : 'oc_collapse_details')}
                        >
                            <div className={bem('orderDetails')}>
                                <LineItemGroup className={bem('lineItemGroup')}>
                                    {lineItems.map((item) => (
                                        <LineItem key={item.orderItemId} {...item} messages={messages} priceComponent={Price}>
                                            {item.finalSale && (
                                                <Notification className={bem('info')} type="info" message={messages.notifications.finalSale()} />
                                            )}
                                        </LineItem>
                                    ))}
                                </LineItemGroup>

                                <h3 className={bem('orderSummaryHeader')}>{messages.orderReview.summaryTitle()}</h3>
                                {totals && (
                                    <Totals
                                        className={bem('totals')}
                                        {...totals}
                                        messages={messages}
                                        noGrandTotalBorder
                                        enablePromotions
                                        taxDutyInfo={totals.dutyType === 'DDU' ? totals.taxDutyInfo : undefined}
                                        displaySavingsExplanation={locale?.country !== 'US' ? true : false}
                                    />
                                )}

                                <div className={bem('shippingBillingInfo')}>
                                    <div className={bem('shippingAddress')}>
                                        <h4 className={bem('shippingBillingInfoHeader')}>{messages.orderConfirmation.shippingTo()}</h4>
                                        <Address {...shippingAddress} className={bem('shippingAddressDetails')} />
                                    </div>
                                    <div className={bem('shippingOption')}>
                                        <h4 className={bem('shippingBillingInfoHeader')}>{messages.orderConfirmation.shippingMethod()}</h4>
                                        <ul className={bem('shippingOptionSummary')}>
                                            <li>{shippingOption.name}</li>
                                            {shippingOption.description.map((line, index) => (
                                                <li key={index}>{line}</li>
                                            ))}
                                            {signatureRequired === 'Y' && <li>{messages.shippingOptionsStep.signatureRequired()}</li>}
                                        </ul>
                                    </div>
                                    <div className={bem('billingInfo')}>
                                        <h4 className={bem('shippingBillingInfoHeader')}>{messages.orderConfirmation.billedTo()}</h4>
                                        <div className={bem('billingSummary')}>
                                            {paymentOption.id === 'COD' && <p className={bem('cod')}>{messages.paymentOptionsForm.cashOnDelivery()}</p>}
                                            {paymentOption.id === 'STORE_CREDIT' && <p className={bem('store-credit')}>{paymentOption.description}</p>}
                                            <PaymentCard
                                                className={bem('cardType')}
                                                card={(paymentOption.type || paymentMethodId)?.replace('_', '')?.toLowerCase()}
                                                key={paymentOption.type}
                                            />
                                            {paymentOption.id?.match(/^KLARNA\w*$/) && (
                                                <p>{messages.paymentOptionsForm?.klarnaOptions({ type: paymentOption.id })}</p>
                                            )}
                                            {paymentOption.lastFourDigits && (
                                                <div className={bem('nameAndDigits')}>
                                                    {cardHolder && <div className={bem('cardHolder')}>{cardHolder}</div>}
                                                    <div className={bem('digits')}>
                                                        <span>{messages.paymentOptionsForm.savedCardDigits(paymentOption)}</span>
                                                        {paymentOption.expireYear && <span>{messages.paymentOptionsForm.savedCardExpiry(paymentOption)}</span>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ToggleSection>
                        {sustainabilityEnabled && <SustainabilityInfo />}
                        <div className={bem('continueShopping', 'registrationEnabled')}>
                            <a href={`/${locale?.id}${continueShoppingPathUrl}`} className={bem('continueShoppingButton')}>
                                {messages.orderConfirmation.continueShopping()}
                            </a>
                        </div>
                    </Fragment>
                ) : (
                    <Redirect to={`/${locale?.id}/checkout`} />
                )}
            </Component>
        )
    }
}

OrderConfirmation.propTypes = propTypes
OrderConfirmation.defaultProps = defaultProps

export default withOptimizely(OrderConfirmation)
